import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import useRipple from "../../../hooks/useRipple";
import "../../../hooks/useRipple.css";
import { IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import { CONFIG_VARS } from "../../../constants/urlConstants/configuration";

import event_card_go_left from "../../../assets/images/event_card_go_left.svg";
import event_card_go_right from "../../../assets/images/event_card_go_right.svg";
import event_card_folderTab from "../../../assets/images/event_card_folderTab.svg";
import event_card_folderTab_active from "../../../assets/images/event_card_folderTab_active.svg";
import "./EventsSection.css";

const EventsSection = ({
  events,
  onEventClick,
  eventLoader = {},
  activeEvent = null,
  refetchEventStudents,
  isFetchingEventStudents = false,
  hideFolderTab = false,
  schoolId = null,
}) => {
  const scrollContainerRef = useRef(null);
  const leftButtonRef = useRef(null);
  const rightButtonRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const resizeObserverRef = useRef(null);
  const [showCopyNotification, setShowCopyNotification] = useState(false);

  useRipple(leftButtonRef);
  useRipple(rightButtonRef);

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;

      const isAtStart = scrollLeft <= 1;
      const isAtEnd = Math.abs(scrollWidth - (scrollLeft + clientWidth)) <= 1;

      setCanScrollLeft(!isAtStart);
      setCanScrollRight(!isAtEnd && scrollWidth > clientWidth);
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      resizeObserverRef.current = new ResizeObserver(() => {
        requestAnimationFrame(checkScroll);
      });

      resizeObserverRef.current.observe(scrollContainer);
      if (scrollContainer.parentElement) {
        resizeObserverRef.current.observe(scrollContainer.parentElement);
      }

      const handleScroll = () => {
        requestAnimationFrame(checkScroll);
      };

      scrollContainer.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", handleScroll);

      checkScroll();

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleScroll);
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
      };
    }
  }, [events]);

  // Effect to scroll active event into view
  useEffect(() => {
    if (activeEvent && scrollContainerRef.current) {
      const activeCard =
        scrollContainerRef.current.querySelector(".event-card.active");
      if (activeCard) {
        activeCard.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [activeEvent, events]);

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = 180;
      const gap = 16;
      const scrollAmount = cardWidth + gap;

      const newScrollPosition =
        container.scrollLeft +
        (direction === "left" ? -scrollAmount : scrollAmount);

      container.scrollTo({
        left: newScrollPosition,
        behavior: "smooth",
      });

      setTimeout(() => {
        requestAnimationFrame(checkScroll);
      }, 500);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const parseEventString = (eventString) => {
    const roundSplit = eventString.split("-Round");
    const roundNumber = roundSplit.length > 1 ? `Round${roundSplit[1]}` : "";
    const beforeRound = roundSplit[0];

    const roundKeywords = ["Semi", "Prelims", "Finals"];
    let roundStartIndex = -1;
    let roundKeyword = "";

    for (const keyword of roundKeywords) {
      const index = beforeRound.indexOf(`-${keyword}`);
      if (index !== -1) {
        roundStartIndex = index;
        roundKeyword = keyword;
        break;
      }
    }

    if (roundStartIndex === -1) {
      return { name: beforeRound, round: "", roundNumber };
    }

    const name = beforeRound.substring(0, roundStartIndex);
    const round = beforeRound.substring(roundStartIndex + 1);

    return { name, round, roundNumber };
  };

  const generateEventDeeplink = (schoolId, event) => {
    if (!schoolId || !event) return "";

    const currentEnv = CONFIG_VARS.currentEnvironment;

    // Check if this is a "Scholarship Test" event (case insensitive)
    const isAtreyaTest =
      event.event.toLowerCase().includes("atreya scholarship test") ||
      event.event.toLowerCase().includes("atreya");

    const isScholarshipTest =
      event.event.toLowerCase().includes("scholarship test") && !isAtreyaTest;
    // Select the appropriate base URL based on event type
    let baseUrl;

    if (isAtreyaTest) {
      baseUrl = CONFIG_VARS.learnScholarshipTestBaseUrl[currentEnv];
    } else if (isScholarshipTest) {
      baseUrl = CONFIG_VARS.scholarshipTestBaseUrl[currentEnv];
    } else {
      baseUrl = CONFIG_VARS.deeplinkBaseUrl[currentEnv];
    }
    const { name, round, roundNumber } = parseEventString(event.event);

    const roundNum = roundNumber.replace("Round", "").trim();
    const level = round;
    const encodedEventName = name.replace(/\s/g, "%20");

    const params = [
      `school_id=${schoolId}`,
      `event=${encodedEventName}`,
      `level=${level}`,
      `round=${roundNum}`,
    ];

    return `${baseUrl}deeplink/register?${params.join("&")}`;
  };

  const sortedEvents = events
    ? [...events].sort((a, b) => {
        return new Date(b.start_date_time) - new Date(a.start_date_time);
      })
    : [];

  return (
    <div className="events-section">
      {sortedEvents.length > 0 && (
        <div className="events-container">
          <div className="events-scroll-container" ref={scrollContainerRef}>
            {sortedEvents.map((event, index) => {
              const { name, round, roundNumber } = parseEventString(
                event.event,
              );
              const isLoading = eventLoader[event.event];
              const isActive =
                activeEvent &&
                activeEvent.event === event.event &&
                activeEvent.start_date_time === event.start_date_time;
              const cardKey = `${event.event}-${event.start_date_time}`;
              return (
                <motion.div
                  key={cardKey}
                  className={`event-card ${isActive ? "active" : ""} ${
                    isLoading ? "loading" : ""
                  }`}
                  style={{
                    position: "relative",
                  }}
                >
                  {isActive && (
                    <motion.div
                      layoutId="active-event-indicator"
                      className="active-event-indicator"
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 40,
                      }}
                    />
                  )}
                  <motion.div
                    className="event-card-content"
                    onClick={() => onEventClick && onEventClick(event)}
                    style={{
                      cursor: onEventClick ? "pointer" : "default",
                    }}
                    whileTap={{ scale: 0.9 }}
                  >
                    {!hideFolderTab && (
                      <div className="event-folder-tab-container">
                        <img
                          src={
                            isActive
                              ? event_card_folderTab_active
                              : event_card_folderTab
                          }
                          alt="folder tab"
                          className="event-folder-tab"
                        />
                        <span className="folder-tab-text">
                          {event.event_type.charAt(0).toUpperCase() +
                            event.event_type.slice(1).toLowerCase()}
                        </span>
                      </div>
                    )}
                    {name.length >= 20 ? (
                      <Tooltip title={name} placement="top" arrow>
                        <div className="event-name">{name}</div>
                      </Tooltip>
                    ) : (
                      <div className="event-name">{name}</div>
                    )}
                    <div className="event-round">{`${round}-${roundNumber}`}</div>
                    <div className="event-date">
                      {formatDate(event.start_date_time)}
                    </div>
                  </motion.div>
                  {refetchEventStudents && isActive && (
                    <Tooltip
                      title="Refresh"
                      placement="right"
                      slots={{
                        transition: Zoom,
                      }}
                      arrow
                    >
                      <span>
                        <IconButton
                          className={`refresh-button ${
                            isFetchingEventStudents ? "spinning" : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.nativeEvent.preventDefault?.();
                            e.nativeEvent.stopImmediatePropagation?.();
                            refetchEventStudents();
                          }}
                          size="small"
                          disabled={isFetchingEventStudents}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {isActive && schoolId && (
                    <div className="event-deeplink-button-container">
                      <button
                        className="event-copy-deeplink-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.nativeEvent.preventDefault?.();
                          e.nativeEvent.stopImmediatePropagation?.();
                          const deeplink = generateEventDeeplink(
                            schoolId,
                            event,
                          );
                          if (deeplink) {
                            navigator.clipboard.writeText(deeplink);
                            setShowCopyNotification(true);
                            setTimeout(() => {
                              setShowCopyNotification(false);
                            }, 2000);
                          }
                        }}
                      >
                        Copy Event Register Deeplink
                      </button>
                      <AnimatePresence>
                        {showCopyNotification && (
                          <motion.div
                            className="event-copy-notification"
                            initial={{ opacity: 0, y: 10, x: "-50%" }}
                            animate={{ opacity: 1, y: 0, x: "-50%" }}
                            exit={{ opacity: 0, y: -10, x: "-50%" }}
                            transition={{ duration: 0.25, ease: "easeOut" }}
                          >
                            Link Copied
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                  {isLoading && <div className="event-loader">Loading...</div>}
                </motion.div>
              );
            })}
          </div>

          <button
            ref={leftButtonRef}
            className="scroll-button left"
            onClick={() => handleScroll("left")}
            disabled={!canScrollLeft}
            aria-label="Scroll left"
          >
            <img src={event_card_go_left} alt="Scroll left" />
          </button>
          <button
            ref={rightButtonRef}
            className="scroll-button right"
            onClick={() => handleScroll("right")}
            disabled={!canScrollRight}
            aria-label="Scroll right"
          >
            <img src={event_card_go_right} alt="Scroll right" />
          </button>
        </div>
      )}
    </div>
  );
};

export default EventsSection;
